import dayjs from 'dayjs';
// Types
import { CreatePostImagePresignedUrlV2_createPostImagePresignedUrlV2 } from 'api/mediaPost/types/CreatePostImagePresignedUrlV2';
import { GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities } from 'api/mediaPost/types/GetVideoEditRequestsForStore';
import {
  GetMediaPost_getMediaPost,
  GetMediaPost_getMediaPost_VideoPost,
} from 'api/mediaPost/types/GetMediaPost';
import { GetNotReviewedInterviewAnswerVideos_getNotReviewedInterviewAnswerVideos } from 'api/interview/types/GetNotReviewedInterviewAnswerVideos';
import { MediaPostType } from 'api/graphql-global-types';
import { VideoDetails } from 'components/common3/VideoDetails/VideoDetails';
// Helpers
import { formatVideoDetailsDate } from 'helpers/dateText';

export type ImageToUpload = {
  fields: string;
  url: string;
  name: string;
  type: string;
  file: File | '';
};

export type UploadImage = {
  data_url?: string;
  file?: File;
};

export const getImageToUpload = (
  preSignedUrl: CreatePostImagePresignedUrlV2_createPostImagePresignedUrlV2,
  image: UploadImage
): ImageToUpload => {
  const { file } = image;

  const imageExtension = file
    ? file.name.split('.')[file.name.split('.').length - 1]
    : '';

  const imageName = `${preSignedUrl?.key || ''}.${imageExtension}`;

  const res: ImageToUpload = {
    fields: preSignedUrl?.fields || '',
    url: preSignedUrl?.url || '',
    name: imageName,
    type: file?.type || '',
    file: file || '',
  };

  return res;
};

export const uploadImageToS3 = async (
  preSignedUrl: CreatePostImagePresignedUrlV2_createPostImagePresignedUrlV2,
  imageToUpload: UploadImage
): Promise<string> => {
  const { fields, url, name: imageName, type, file } = getImageToUpload(
    preSignedUrl,
    imageToUpload
  );

  const formData = new FormData();

  Object.entries(JSON.parse(fields)).forEach(([key, value]) => {
    formData.append(key, value as string);
  });

  const parts = imageName.split('.');
  const newImageName = parts.slice(0, -1).join('.');

  formData.append('key', newImageName);
  formData.append('Content-Type', type);
  formData.append('file', file);

  await fetch(url, {
    method: 'POST',
    body: formData,
  });

  return newImageName;
};

export const getDateDifference = (created: Date) => {
  const today = new Date();

  // difference between dates divided by a day which is 24 hours, 60 min,  60 sek and 1000 milliseconds
  const day = Math.floor(
    (today.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)
  );
  const month = Math.abs(today.getMonth() - created.getMonth());
  const year = Math.abs(today.getFullYear() - created.getFullYear());

  if (year) {
    switch (year) {
      case 1:
        return 'Last Year';
      default:
        return year + ' Years Ago';
    }
  }

  if (month && day > 31) {
    switch (month) {
      case 1:
        return 'Last Month';
      default:
        return month + ' Months Ago';
    }
  }

  if (day <= 31) {
    switch (day) {
      case 0:
        return 'Today'; // if less than 24h passed
      case 1:
        return 'Yesterday'; // if less than 48h had passed
      default:
        return day + ' Days Ago';
    }
  }
};

export type MediaFilter = 'image' | 'video' | 'all';

export const getUrlType = (url: string): MediaFilter => {
  if (url === 'video' || url === 'image') {
    return url;
  } else {
    return 'all';
  }
};

export const getLocaleDate = (dateTime: string): string => {
  const formattedDate = dayjs(dateTime).format('DD MMM YYYY');

  return formattedDate;
};

export const getVideoPostDetails = (
  videoPost: GetMediaPost_getMediaPost_VideoPost
): VideoDetails => {
  return {
    views: videoPost.views?.viewCount || 0,
    date: formatVideoDetailsDate(videoPost?.scheduledAt ?? videoPost.createdAt),
    description: videoPost.body || null,
    mentions: videoPost.mentions || [],
    hashtags: videoPost.hashtags || [],
  };
};

export type State = {
  mediaFilters: MediaFilter;
  page: number;
  reviewModalVisibility: boolean;
  videoRequestItem: GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities | null;
  interviewAnswerVideoModalVisibility: boolean;
  interviewAnswerVideoItem: GetNotReviewedInterviewAnswerVideos_getNotReviewedInterviewAnswerVideos | null;
};

export type Action =
  | { type: 'SET_FILTER'; payload: MediaFilter }
  | { type: 'INCREMENT_PAGE' }
  | { type: 'RESET_PAGE' }
  | {
      type: 'SHOW_REVIEW_MODAL';
      payload: GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities;
    }
  | { type: 'HIDE_REVIEW_MODAL' }
  | {
      type: 'SHOW_INTERVIEW_ANSWER_VIDEO_MODAL';
      payload: GetNotReviewedInterviewAnswerVideos_getNotReviewedInterviewAnswerVideos;
    }
  | { type: 'HIDE_INTERVIEW_ANSWER_VIDEO_MODAL' };

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_FILTER':
      return {
        ...state,
        mediaFilters: action.payload,
        page: 1,
      };
    case 'INCREMENT_PAGE':
      return {
        ...state,
        page: state.page + 1,
      };
    case 'RESET_PAGE':
      return {
        ...state,
        page: 1,
      };
    case 'SHOW_REVIEW_MODAL':
      return {
        ...state,
        reviewModalVisibility: true,
        videoRequestItem: action.payload,
      };
    case 'HIDE_REVIEW_MODAL':
      return {
        ...state,
        reviewModalVisibility: false,
        videoRequestItem: null,
      };
    case 'SHOW_INTERVIEW_ANSWER_VIDEO_MODAL':
      return {
        ...state,
        interviewAnswerVideoModalVisibility: true,
        interviewAnswerVideoItem: action.payload,
      };
    case 'HIDE_INTERVIEW_ANSWER_VIDEO_MODAL':
      return {
        ...state,
        interviewAnswerVideoModalVisibility: false,
        interviewAnswerVideoItem: null,
      };
    default:
      return state;
  }
};

export const getMediaImage = (
  mediaFeed: GetMediaPost_getMediaPost
): string | undefined => {
  const isVideo = 'video' in mediaFeed;

  if (isVideo) {
    return (
      mediaFeed?.thumbnailUrl || mediaFeed?.video?.thumbnailUrl || undefined
    );
  }

  return mediaFeed.image?.url || undefined;
};

export const getMediaType = (
  mediaFeed: GetMediaPost_getMediaPost
): MediaPostType => {
  return 'video' in mediaFeed
    ? MediaPostType.VideoPost
    : MediaPostType.ImagePost;
};

export const isVideoPost = (mediaFeed: GetMediaPost_getMediaPost): boolean => {
  return 'video' in mediaFeed;
};

export const isImagePost = (mediaFeed: GetMediaPost_getMediaPost): boolean => {
  return 'image' in mediaFeed;
};
